import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { CSVLink } from "react-csv";
import Swal from "sweetalert2";
import { NestedMenuItem } from "mui-nested-menu";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import styles from "../components/sidebar/Sidebar.module.css";
import ParetoModal from "./dataTable/modals/ParetoModal";
import ScatterPlotModal from "./dataTable/modals/ScatterPlotModal";
import useSaveDocument from "../hooks/useSaveDocument";
import MultipleVariableModal from "./dataTable/modals/MultipleVariableModal";
import LinearRegressionModal from "./dataTable/modals/LinearRegressionModal";
import ScatterPlotEDAModal from "./dataTable/modals/ScatterPlotEDAModal";
import MainModal from "./analysis/modals/MainModal";

const MENU_ITEM_STYLE = { fontSize: "13px", fontWeight: "bolder" };

const ANALYSIS_ARRAY = [
  "Basic Statistics",
  "Correlation",
  "ANOVA",
  "Time Series",
];

const GRAPHS_ARRAY = [
  "Boxplot",
  "Histogram",
  "Control Charts",
  "Pareto Chart",
  "Simple Scatterplot",
  "Individual Value Plot",
  "Linear Regression",
  "Scatterplot EDA (Exploratory Data Analysis)",
];

const BASIC_STATISTICS_ARRAY = [
  "Descriptive Statistics",
  "Graphical Summary",
  "1-Sample t",
  "2-Sample t",
  "Paired t",
  "1-Proportion Test",
  "Normality Test",
  "Chi Square Test",
  "1 Variance Test",
  "2-Variance Test",
];

const CORRELATION_ARRAY = ["Simple Correlation", "Multiple Correlation"];

const ANOVA_ARRAY = ["ANOVA (One-way)"];

const TIME_SERIES_ARRAY = [
  "[Single Price] Time Series Plot",
  "[Single Price] Time Series Plot with Group",
  "[All Prices] Time Series Plot",
  "[All Prices] Time Series Plot with Group",
];

const BOXPLOT_ARRAY = [
  "Simple Box Plot (Group)",
  "Simple Box Plot (Ungroup)",
  "Multi Variable Boxplot (Group)",
  "Multi Variable Boxplot (Ungroup)",
];
const HISTOGRAM_ARRAY = [
  "Simple Histogram",
  "Simple Histogram (with distribution)",
  "Multi Variable Histogram",
  "Multi Variable Histogram (with distribution)",
];

const CONTROL_CHARTS_ARRAY = ["Variables", "Attributes"];

const ActionButton = ({ children, sx, actionName }) => {
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const { saveDocument } = useSaveDocument();
  const { data: csvData, fileName } = useSelector((state) => state.dataTable);

  const handleClick = (event) => {
    if (location.pathname.split("/")[1] === "manage") {
      return;
    }
    setOpen(true);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = async () => {
    setOpen(false);
    setAnchorEl(null);
    setOpenModal(false);
    await saveDocument();
  };

  const handleSaveFile = async () => {
    try {
      await saveDocument();
    } catch (error) {
      Swal.fire(
        "Sorry",
        "There was an error with saving the document",
        "error"
      );
    }
    Swal.fire("Success", "Document is saved", "success");
  };

  const handleMenuItem = (item) => {
    switch (item) {
      case "Basic Statistics":
        return BASIC_STATISTICS_ARRAY;
      case "Correlation":
        return CORRELATION_ARRAY;
      case "ANOVA":
        return ANOVA_ARRAY;
      case "Time Series":
        return TIME_SERIES_ARRAY;
      case "Boxplot":
        return BOXPLOT_ARRAY;
      case "Histogram":
        return HISTOGRAM_ARRAY;
      case "Control Charts":
        return CONTROL_CHARTS_ARRAY;
      default:
        return [];
    }
  };

  const VARIABLES_ARRAY = ["X-Bar and S", "X-Bar and R"];
  const ATTRIBUTES_ARRAY = [
    "C-Chart",
    "I-MR Chart",
    "U-Chart",
    "NP-Chart",
    "P-Chart",
  ];

  const handleControlCharts = (item) => {
    switch (item) {
      case "Variables":
        return VARIABLES_ARRAY;
      case "Attributes":
        return ATTRIBUTES_ARRAY;
      default:
        return [];
    }
  };

  const handleGraphsMenu = (item, index) => {
    return item === "Variables" || item === "Attributes" ? (
      <NestedMenuItem
        key={index}
        label={item}
        parentMenuOpen={open}
        sx={MENU_ITEM_STYLE}
      >
        {handleControlCharts(item).map((variable, i) => {
          return (
            <MenuItem
              key={i}
              onClick={() => {
                setOpenModal(variable);
              }}
              style={MENU_ITEM_STYLE}
            >
              {variable}
            </MenuItem>
          );
        })}
      </NestedMenuItem>
    ) : (
      <MenuItem
        key={index}
        onClick={() => {
          setOpenModal(item);
        }}
        style={MENU_ITEM_STYLE}
      >
        {item}
      </MenuItem>
    );
  };

  return (
    <>
      <Box
        className={styles.action_button}
        sx={{
          width: "70px",
          height: "70px",
          margin: "10px auto",
          borderRadius: "22px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          boxShadow: "0px 0px 5px 0px rgba(0,0,0,0.20)",
          textAlign: "center",
          fontSize: "10px",
          "&:hover": {
            backgroundColor: "#eefbee",
            cursor: "pointer",
          },
          ...sx,
        }}
        id="basic-button"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
      >
        {children}
      </Box>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        style={{
          marginLeft: "80px",
          marginTop: "-70px",
        }}
        disableEnforceFocus
      >
        {actionName === "analysis" && (
          <div>
            {ANALYSIS_ARRAY.map((analysis, i) => {
              return (
                <NestedMenuItem
                  key={i}
                  label={analysis}
                  parentMenuOpen={open}
                  sx={MENU_ITEM_STYLE}
                >
                  {handleMenuItem(analysis).map((item, index) => {
                    return (
                      <MenuItem
                        key={index}
                        onClick={() => {
                          setOpenModal(item);
                        }}
                        style={MENU_ITEM_STYLE}
                      >
                        {item}
                      </MenuItem>
                    );
                  })}
                </NestedMenuItem>
              );
            })}
          </div>
        )}

        {actionName === "file" && (
          <div>
            <Link to="/new" target="_blank" rel="noopener noreferrer">
              <MenuItem style={MENU_ITEM_STYLE}>Create new file</MenuItem>
            </Link>

            <Link
              to={"/manage/files"}
              target="_blank"
              rel="noopener noreferrer"
            >
              <MenuItem style={MENU_ITEM_STYLE}>Open Existing file</MenuItem>
            </Link>

            <MenuItem style={MENU_ITEM_STYLE} onClick={handleSaveFile}>
              Save file
            </MenuItem>

            <CSVLink
              data={csvData}
              target="_blank"
              filename={`${fileName}.csv`}
            >
              <MenuItem style={MENU_ITEM_STYLE}>Download data (.csv)</MenuItem>
            </CSVLink>

            {/* Back to menu */}
            <Link to="/">
              <MenuItem style={MENU_ITEM_STYLE}>Back to menu</MenuItem>
            </Link>
          </div>
        )}

        {actionName === "graph" && (
          <>
            {GRAPHS_ARRAY.map((graph, i) => {
              return i < 3 ? (
                <NestedMenuItem
                  key={i}
                  label={graph}
                  parentMenuOpen={open}
                  sx={MENU_ITEM_STYLE}
                >
                  {handleMenuItem(graph).map((item, index) => {
                    return handleGraphsMenu(item, index);
                  })}
                </NestedMenuItem>
              ) : (
                <MenuItem
                  key={i}
                  onClick={() => {
                    setOpenModal(graph);
                  }}
                  style={MENU_ITEM_STYLE}
                >
                  {graph}
                </MenuItem>
              );
            })}
          </>
        )}

        {actionName === "help" && (
          <div>
            <MenuItem style={MENU_ITEM_STYLE}>Contact Us</MenuItem>
            <MenuItem style={MENU_ITEM_STYLE}>About Alphatuos</MenuItem>
          </div>
        )}
      </Menu>

      <MainModal
        handleClose={handleClose}
        openModal={openModal}
        setOpenModal={setOpenModal}
      />
    </>
  );
};

export default ActionButton;
